/**
 * Control Panel Settings - التحكم بلوحة التحكم والمعلومات
 * Hub of ASB606 ADMIN control cards.
 * Job Titles moved to /control-panel-settings/dropdowns/job-titles (Dropdown Lists hub).
 */
import { useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Settings, List, Sliders, FileText, Images, Briefcase, Upload, AlertTriangle } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import DashboardLayout from '../components/DashboardLayout';
import { toast } from 'sonner';
import { usePerms } from '../permissions/PermissionsContext';

export default function ControlPanelSettings() {
  const { language } = useLanguage();
  const navigate = useNavigate();
  const { visiblePages } = usePerms();
  const [searchParams] = useSearchParams();

  // Backward-compat: old deep-link ?tab=job-titles → new dedicated page
  useEffect(() => {
    if (searchParams.get('tab') === 'job-titles') {
      navigate('/control-panel-settings/dropdowns/job-titles', { replace: true });
    }
  }, [searchParams, navigate]);

  const platformButtons = [
    {
      id: 'master-docs',
      titleAr: 'قائمة الأوراق المطلوبة',
      titleEn: 'Required Documents List',
      descAr: 'قائمة الأوراق المركزية + خيار الهوية حسب الجنسية',
      descEn: 'Central docs list + nationality-based ID option',
      icon: FileText,
      color: '#F59E0B',
      code: 'CPL-005',
      route: '/control/asb606-admin/master-documents'
    },
    // Jun 2026 — master list of service violations (per-service × emirate copies, like required docs)
    {
      id: 'master-violations',
      titleAr: 'قائمة مخالفات الخدمات',
      titleEn: 'Service Violations List',
      descAr: 'نوع المخالفة، الاحتساب اليومي/الثابت، بداية التسعيرة، السقف والمهلة — تُطبّق على كل خدمة وإمارة',
      descEn: 'Violation type, daily/fixed calculation, starting fee, cap & grace period — applied per service & emirate',
      icon: AlertTriangle,
      color: '#F43F5E',
      code: 'CPL-009',
      route: '/control/asb606-admin/master-violations'
    },
    {
      id: 'dropdowns',
      titleAr: 'القوائم المنسدلة',
      titleEn: 'Dropdown Lists',
      descAr: 'إدارة القوائم المنسدلة (المسميات الوظيفية، جهات مدفوعات الشركة...)',
      descEn: 'Manage dropdown lists (job titles, company payment entities...)',
      icon: List,
      color: '#06B6D4',
      code: 'CPL-006',
      route: '/control-panel-settings/dropdowns'
    },
    {
      id: 'image-library',
      titleAr: 'رفع الصور',
      titleEn: 'Image Library',
      descAr: 'رفع صور وأخذ رابط مباشر لاستخدامها في الإيميلات والمواقع الخارجية',
      descEn: 'Upload images and get direct URLs for use in emails and external channels',
      icon: Images,
      color: '#EC4899',
      code: 'CPL-007',
      route: '/control-panel-settings/image-library'
    },
    // iter-236 (Feb 2026) — Admin file uploads (Emergent Object Storage).
    {
      id: 'file-uploads',
      titleAr: 'رفع الملفات',
      titleEn: 'File Uploads',
      descAr: 'ارفع أي ملف (PDF، صور، وثائق) واحصل على رابط تحميل مباشر',
      descEn: 'Upload any file (PDF, images, docs) and get a direct download link',
      icon: Upload,
      color: '#D4AF37',
      code: 'CPL-008',
      route: '/control/files',
    },
    // iter-218c (Feb 2026) — "التحكم بإدارة الشركة" moved HERE from the
    // CLOUDGM FZ LLC (Control) hub per user's explicit request. Replaced
    // the old "coming soon" Company Settings card.
    {
      id: 'company-mgmt',
      titleAr: 'التحكم بإدارة الشركة',
      titleEn: 'Company Management Control',
      descAr: 'الشهادات + إعدادات الموظفين (التوقيع والختم) + اسم الشركة الرسمي',
      descEn: 'Certificates + employee settings (signature & stamp) + official company name',
      icon: Briefcase,
      color: '#F59E0B',
      code: 'CPL-003',
      route: '/control-panel-settings/company-mgmt'
    },
    {
      id: 'interface-settings',
      titleAr: 'إعدادات الواجهة',
      titleEn: 'Interface Settings',
      descAr: 'تخصيص مظهر لوحة التحكم',
      descEn: 'Customize dashboard appearance',
      icon: Settings,
      color: '#3B82F6',
      code: 'CPL-004',
      comingSoon: true
    }
  ];

  return (
    <DashboardLayout
      title={language === 'ar' ? 'ASB606 ADMIN (للتحكم)' : 'ASB606 ADMIN (Control)'}
      titleEn="ASB606 ADMIN (Control)"
      subtitle={language === 'ar' ? 'إدارة إعدادات المنصات' : 'Manage platform settings'}
      subtitleEn="Manage platform settings"
      screenCode="ASB-005"
      onBack={() => navigate('/dashboard?view=platform-control')}
      breadcrumb={[
        { labelAr: 'التحكم والمعلومات للمنصات', labelEn: 'Platform Control & Info', path: '/dashboard?view=platform-control' },
        { labelAr: 'ASB606 ADMIN (للتحكم)', labelEn: 'ASB606 ADMIN (Control)', path: null }
      ]}
    >
      <section className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <h2 className="text-lg font-bold text-white mb-6 flex items-center gap-2">
            <Sliders className="h-5 w-5 text-[#d4af37]" />
            {language === 'ar' ? 'إعدادات ASB606 ADMIN' : 'ASB606 ADMIN Settings'}
          </h2>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 gap-6">
            {visiblePages(platformButtons, 'route').map((item) => (
              <motion.div
                key={item.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                whileHover={{ y: -5 }}
                onClick={() => {
                  if (item.comingSoon) {
                    toast.info(language === 'ar' ? 'قريباً' : 'Coming Soon');
                  } else if (item.route) {
                    navigate(item.route);
                  }
                }}
                className="group relative bg-white/[0.02] backdrop-blur-xl rounded-2xl p-6 border border-white/10 hover:border-[#d4af37]/50 transition-all duration-300 cursor-pointer"
                data-testid={`cpl-card-${item.id}`}
              >
                {/* Screen Code Badge */}
                <div className="absolute top-3 left-3 px-2 py-0.5 bg-white/5 rounded-md">
                  <span className="text-[10px] font-mono text-[#71717A]">{item.code}</span>
                </div>

                {item.comingSoon && (
                  <div className="absolute top-3 right-3 px-2 py-0.5 bg-amber-500/10 rounded-md">
                    <span className="text-[10px] text-amber-400">{language === 'ar' ? 'قريباً' : 'Soon'}</span>
                  </div>
                )}

                <div
                  className="w-16 h-16 rounded-xl flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform duration-300"
                  style={{ backgroundColor: `${item.color}15`, border: `1px solid ${item.color}30` }}
                >
                  <item.icon className="h-8 w-8" style={{ color: item.color }} />
                </div>
                <h3 className="text-base font-bold text-white text-center mb-1">
                  {language === 'ar' ? item.titleAr : item.titleEn}
                </h3>
                <p className="text-xs text-[#71717A] text-center">
                  {language === 'ar' ? item.descAr : item.descEn}
                </p>
              </motion.div>
            ))}
          </div>
        </motion.div>
      </section>
    </DashboardLayout>
  );
}
